<!--
 * @Description: 备付金账户中心（收钱吧）
-->
<template>
  <scroll-layout class="provision-account-list">
    <template slot="header">
      <!-- 账户余额 -->
      <div class="account-balance-bar">
        <span class="label">账户余额：</span>
        <span class="amount">¥{{ statistics.balance?.toFixed(2) || '0.00' }}</span>
        <el-tooltip class="balance-tooltip" effect="dark" placement="right">
          <div slot="content" class="balance-tooltip-content">
            <p>1、【超期退款】会让账户实际余额出现大于理论余额（超期退款走线下打款）；</p>
            <p>
              2、【后台充值】会让账户实际余额出现小于理论余额（后台充值并没有实际收入，但会产生充电支出）。
            </p>
          </div>
          <i class="el-icon-warning-outline"></i>
        </el-tooltip>
        <!-- 充值入口暂时下线 -->
      </div>

      <!-- 查询条件 -->
      <ykc-search
        ref="YkcSearch"
        :data="searchData"
        :searchForm="searchParams"
        @clean="handleSearchClean"
        @click="handleSearchConfirm"></ykc-search>
    </template>

    <!-- 收支统计 -->
    <div class="account-statistics">
      <span class="income">账户收入：¥{{ statistics.totalIncome?.toFixed(2) || '0.00' }}</span>
      <span class="expenditure">
        账户支出：¥{{ statistics.totalOutcome?.toFixed(2) || '0.00' }}
      </span>
    </div>

    <ykc-table
      ref="YkcTable"
      :data="tableData"
      :title="tableTitle"
      :columns="tableColumns"
      :operateButtons="tableOperateButtons.filter(item => item.enabled())">
      <template #headerButtons>
        <div class="table-header-button">
          <ykc-button v-rbac="'settlement:manage:provision:export'" type="plain" @click="doExport">
            导出
          </ykc-button>
        </div>
      </template>

      <ykc-pagination
        align="right"
        slot="pagination"
        ref="YkcPagination"
        :total="pageInfo.total"
        :page-size.sync="pageInfo.size"
        :current-page.sync="pageInfo.current"
        @size-change="handlePaginationSizeChange"
        @current-change="handlePaginationCurrentChange"></ykc-pagination>
    </ykc-table>
  </scroll-layout>
</template>

<script>
  import { offlineExport } from '@/utils';
  import { provisionAccount } from '@/service/apis';

  const FLOW_TYPE_OPTIONS = [
    { id: '1', name: '银行转账充值金额' },
    { id: '2', name: '银行转账充值入金手续费' },
    { id: '3', name: '微信转出金额' },
    { id: '4', name: '微信转出入金手续费' },
    { id: '5', name: '清分' },
  ];

  const FLOW_DIRECTION_OPTIONS = [
    { id: '1', name: '收入' },
    { id: '2', name: '支出' },
  ];

  const FLOW_TARGET_OPTIONS = [
    { id: '1', name: '备付金账户' },
    { id: '2', name: '微信商户号' },
    { id: '3', name: '平台子账户' },
  ];

  export default {
    name: 'ProvisionAccountList',
    data() {
      return {
        tableTitle: '账户流水记录',
        tableData: [],
        tableOperateButtons: [],
        tableColumns: [
          {
            label: '流水号',
            prop: 'flowNumber',
            minWidth: '180px',
            scopedSlots: {
              default: ({ row }) => this.renderFlowNoCell(row.flowNumber),
            },
          },
          { label: '流水时间', prop: 'flowTime', minWidth: '160px' },
          {
            label: '流水金额（元）',
            prop: 'flowAmount',
            minWidth: '130px',
            scopedSlots: {
              default: ({ row }) => this.formatAmount(row.flowAmount),
            },
          },
          {
            label: '流水类型',
            prop: 'flowTypeStr',
            minWidth: '160px',
          },
          {
            label: '收支方向',
            prop: 'flowDirectionStr',
            minWidth: '100px',
          },
          {
            label: '交易后余额（元）',
            prop: 'balanceAfter',
            minWidth: '140px',
            scopedSlots: {
              default: ({ row }) => this.formatAmount(row.balanceAfter),
            },
          },
          {
            label: '流水对象',
            prop: 'flowTargetTypeStr',
            minWidth: '120px',
          },
          {
            label: '回执单号',
            prop: 'receiptNo',
            minWidth: '180px',
            scopedSlots: {
              default: ({ row }) => row.receiptNo || '—',
            },
          },
        ],
        pageInfo: {
          current: 1,
          size: 10,
          total: 0,
        },
        searchParams: {
          flowNumber: '',
          flowType: '',
          flowDirection: '',
          flowTarget: '',
          flowTimeRange: [],
        },
        statistics: {
          balance: 0,
          totalIncome: 0,
          totalOutcome: 0,
        },
      };
    },
    computed: {
      searchData() {
        const all = { id: '', name: '全部' };
        return [
          {
            comName: 'YkcInput',
            key: 'flowNumber',
            label: '流水号',
            placeholder: '请输入流水号',
          },
          {
            comName: 'YkcDropdown',
            key: 'flowType',
            label: '流水类型',
            placeholder: '请选择流水类型',
            data: [all, ...FLOW_TYPE_OPTIONS],
          },
          {
            comName: 'YkcDropdown',
            key: 'flowDirection',
            label: '收支方向',
            placeholder: '请选择收支方向',
            data: [all, ...FLOW_DIRECTION_OPTIONS],
          },
          {
            comName: 'YkcDropdown',
            key: 'flowTarget',
            label: '流水对象',
            placeholder: '请选择流水对象',
            data: [all, ...FLOW_TARGET_OPTIONS],
          },
          {
            comName: 'YkcDatePicker',
            key: 'flowTimeRange',
            label: '流水时间',
            type: 'daterange',
            startPlaceholder: '开始日期',
            endPlaceholder: '结束日期',
          },
        ];
      },
    },
    created() {
      this.initData();
      this.requestStatistics();
      this.requestList();
    },
    methods: {
      initData() {
        this.tableOperateButtons = [];
      },
      formatAmount(value) {
        if (value === undefined || value === null || value === '') {
          return '—';
        }
        const num = Number(value);
        return Number.isNaN(num) ? value : `¥${num.toFixed(2)}`;
      },
      /**
       * 渲染流水号单元格（鼠标悬停显示复制按钮）
       */
      renderFlowNoCell(flowNumber) {
        if (!flowNumber) {
          return '—';
        }
        return (
          <span class="flow-no-cell">
            <span class="flow-no-text" title={flowNumber}>
              {flowNumber}
            </span>
            <i
              class="el-icon-document-copy flow-no-copy-btn"
              title="复制"
              onClick={event => {
                event.stopPropagation();
                this.handleCopyFlowNumber(flowNumber);
              }}></i>
          </span>
        );
      },
      /**
       * 复制流水号
       */
      handleCopyFlowNumber(flowNumber) {
        this.$copyText(flowNumber)
          .then(() => {
            this.$message.success('复制成功');
          })
          .catch(() => {
            this.$message.error('复制失败');
          });
      },
      /**
       * 结束日期补全到当天最后一秒
       */
      formatDateEnd(date) {
        return date ? `${date} 23:59:59` : '';
      },
      /**
       * 查询账户余额及收支统计
       */
      requestStatistics() {
        provisionAccount.statistics({}).then(response => {
          const data = response || {};
          this.statistics = {
            balance: Number(data.balance || 0),
            totalIncome: Number(data.totalIncome || 0),
            totalOutcome: Number(data.totalOutcome || 0),
          };
        });
      },
      /**
       * 请求列表数据
       */
      requestList() {
        const params = {
          flowNumber: this.searchParams.flowNumber,
          flowType: this.searchParams.flowType,
          flowDirection: this.searchParams.flowDirection,
          flowTarget: this.searchParams.flowTarget,
          flowTimeStart: this.searchParams.flowTimeRange?.[0] || '',
          flowTimeEnd: this.formatDateEnd(this.searchParams.flowTimeRange?.[1]),
          current: this.pageInfo.current,
          size: this.pageInfo.size,
        };
        provisionAccount.list(params).then(response => {
          this.tableData = (response && response.records) || [];
          this.pageInfo.total = (response && response.total) || 0;
        });
      },
      /**
       * 点击筛选查询按钮
       */
      handleSearchConfirm(searchData) {
        Object.assign(this.searchParams, searchData);
        this.pageInfo.current = 1;
        this.requestStatistics();
        this.requestList();
      },
      /**
       * 点击清空查询按钮
       */
      handleSearchClean(form) {
        Object.assign(this.searchParams, form);
        this.searchParams.flowTimeRange = [];
      },
      /**
       * 分页大小变化
       */
      handlePaginationSizeChange(size) {
        this.pageInfo.current = 1;
        this.pageInfo.size = size;
        this.requestList();
      },
      /**
       * 分页页码变化
       */
      handlePaginationCurrentChange(current) {
        this.pageInfo.current = current;
        this.requestList();
      },
      /**
       * 导出
       */
      doExport() {
        offlineExport(
          {
            downloadType: 'finance_export',
            jsonNode: {
              downloadKey: 'provisionAccountFlowExport',
              flowNumber: this.searchParams.flowNumber,
              flowType: this.searchParams.flowType,
              flowDirection: this.searchParams.flowDirection,
              flowTarget: this.searchParams.flowTarget,
              flowTimeStart: this.searchParams.flowTimeRange?.[0] || '',
              flowTimeEnd: this.formatDateEnd(this.searchParams.flowTimeRange?.[1]),
            },
          },
          this.pageInfo.total
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  .provision-account-list {
    .account-balance-bar {
      display: flex;
      align-items: baseline;
      padding: 16px 0;
      margin-bottom: 8px;
      background-color: #fff;

      .label {
        font-size: 16px;
        font-weight: 500;
        color: #333;
      }

      .amount {
        font-size: 24px;
        font-weight: bold;
        color: #ff6b00;
      }
    }

    .account-statistics {
      padding: 12px 0;
      margin-bottom: 8px;
      font-size: 14px;
      color: #333;

      .income {
        margin-right: 24px;
        color: #07c160;
      }

      .expenditure {
        color: #ff4d4f;
      }
    }

    // 流水号单元格：鼠标悬停显示复制按钮
    .flow-no-cell {
      display: inline-flex;
      align-items: center;
      max-width: 100%;

      .flow-no-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .flow-no-copy-btn {
        flex-shrink: 0;
        margin-left: 4px;
        color: #1890ff;
        cursor: pointer;
        visibility: hidden;

        &:hover {
          color: #40a9ff;
        }
      }

      &:hover .flow-no-copy-btn {
        visibility: visible;
      }
    }
  }
</style>
